import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Cloud, CheckCircle2, XCircle, Copy, ExternalLink, Play, AlertTriangle, ShieldCheck, Info } from 'lucide-react';
import { cloudConnectionsApi } from '../services/api';
import { Card, SectionHeader, Loader } from '../components/ui';
import { useStore } from '../store';
import toast from 'react-hot-toast';

const GREEN = '#22c55e';
const AMBER = '#ffb800';
const RED   = '#ff5c5c';
const BLUE  = '#4a9eff';

export default function CloudConnectionsPage() {
  const qc = useQueryClient();
  const { activeTenantId } = useStore();

  const { data: conn, isLoading } = useQuery({
    queryKey: ['cloud-conn', activeTenantId],
    queryFn: () => cloudConnectionsApi.get().then((r) => r.data),
    retry: false,
  });

  const [mode, setMode] = useState('ROLE_ARN');
  const [roleArn, setRoleArn] = useState('');
  const [externalId, setExternalId] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [accountId, setAccountId] = useState('');
  const [staticKey, setStaticKey] = useState('');
  const [staticSecret, setStaticSecret] = useState('');
  const [testResult, setTestResult] = useState(null);

  React.useEffect(() => {
    if (conn) {
      setMode(conn.mode === 'STATIC_KEYS' ? 'STATIC_KEYS' : 'ROLE_ARN');
      setRoleArn(conn.awsRoleArn || '');
      setExternalId(conn.awsExternalId || '');
      setRegion(conn.awsRegion || 'us-east-1');
      setAccountId(conn.awsAccountId || '');
    }
  }, [conn]);

  const saveRole = useMutation({
    mutationFn: () => cloudConnectionsApi.saveRole({ roleArn, externalId, region, accountId }),
    onSuccess: () => { toast.success('AWS role connection saved'); qc.invalidateQueries({ queryKey: ['cloud-conn'] }); setTestResult(null); },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });
  const saveKeys = useMutation({
    mutationFn: () => cloudConnectionsApi.saveStaticKeys({ accessKeyId: staticKey, secretAccessKey: staticSecret, region, accountId }),
    onSuccess: () => { toast.success('Static-key connection saved'); qc.invalidateQueries({ queryKey: ['cloud-conn'] }); setStaticKey(''); setStaticSecret(''); },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });
  const test = useMutation({
    mutationFn: () => cloudConnectionsApi.test(),
    onSuccess: (r) => { setTestResult(r.data); if (r.data.success) toast.success('Connection OK'); else toast.error('Connection failed'); },
    onError: () => toast.error('Test failed'),
  });
  const disconnect = useMutation({
    mutationFn: () => cloudConnectionsApi.disconnect(),
    onSuccess: () => { toast.success('Disconnected'); qc.invalidateQueries({ queryKey: ['cloud-conn'] }); setTestResult(null); },
  });

  if (isLoading) return <Loader />;

  const connected = conn && conn.mode !== 'NONE';
  const cfnUrl = `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?stackName=SmalBluReadOnly&templateURL=${encodeURIComponent(cloudConnectionsApi.templateUrl())}`;

  return (
    <div>
      <SectionHeader
        title="Cloud Connections"
        subtitle="Grant SmalBlu read-only access to your AWS account. Preferred method is an IAM role SmalBlu assumes on demand — no long-lived keys stored." />

      {/* Status pill */}
      <Card style={{ padding: 16, marginBottom: 20, borderLeft: `4px solid ${connected ? GREEN : AMBER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {connected
            ? <CheckCircle2 size={20} color={GREEN} />
            : <AlertTriangle size={20} color={AMBER} />}
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
              {connected ? conn.status : 'No AWS connection configured'}
            </div>
            {connected && (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2, fontFamily: 'monospace' }}>
                {conn.awsRoleArn || conn.awsAccessKeyIdMasked} · region {conn.awsRegion}
                {conn.awsAccountId && ` · account ${conn.awsAccountId}`}
              </div>
            )}
          </div>
          {connected && (
            <>
              <button onClick={() => test.mutate()} disabled={test.isLoading}
                style={btn(BLUE)}>
                <Play size={13} /> Test connection
              </button>
              <button onClick={() => { if (window.confirm('Remove AWS connection for this tenant?')) disconnect.mutate(); }}
                style={btn(RED)}>
                Disconnect
              </button>
            </>
          )}
        </div>
        {testResult && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 8,
                        background: testResult.success ? 'rgba(34,197,94,0.08)' : 'rgba(255,92,92,0.08)',
                        border: `1px solid ${testResult.success ? '#22c55e44' : '#ff5c5c44'}`, fontSize: 12 }}>
            {testResult.success ? (
              <>
                <div style={{ color: GREEN, fontWeight: 700, marginBottom: 4 }}>✓ SmalBlu can call AWS as this identity</div>
                <div style={{ fontFamily: 'monospace' }}>Account: {testResult.accountId}</div>
                <div style={{ fontFamily: 'monospace' }}>ARN: {testResult.arn}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Round trip: {testResult.roundTripMs} ms</div>
              </>
            ) : (
              <>
                <div style={{ color: RED, fontWeight: 700, marginBottom: 4 }}>✗ Connection failed</div>
                <div style={{ fontFamily: 'monospace' }}>{testResult.errorMessage}</div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Mode picker */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { id: 'ROLE_ARN',    label: 'IAM Role (recommended)', icon: ShieldCheck },
          { id: 'STATIC_KEYS', label: 'Static keys (legacy)',    icon: AlertTriangle },
        ].map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button key={m.id} onClick={() => setMode(m.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: active ? 'rgba(74,158,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${active ? '#4a9eff99' : 'rgba(255,255,255,0.1)'}`,
                color: active ? '#fff' : 'rgba(255,255,255,0.7)', cursor: 'pointer',
              }}>
              <Icon size={14} /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {mode === 'ROLE_ARN' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
          <Card style={{ padding: 20 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
              Paste your IAM role ARN
            </div>
            <Field label="Role ARN" value={roleArn} onChange={setRoleArn}
              placeholder="arn:aws:iam::123456789012:role/SmalBluReadOnly" mono />
            <Field label="External ID" value={externalId} onChange={setExternalId}
              placeholder="smalblu-your-tenant-slug" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Primary AWS region" value={region} onChange={setRegion} placeholder="us-east-1" />
              <Field label="AWS account ID (optional)" value={accountId} onChange={setAccountId} placeholder="123456789012" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={() => saveRole.mutate()} disabled={!roleArn || saveRole.isLoading}
                style={btnFilled(BLUE)}>Save connection</button>
              <button onClick={() => test.mutate()} disabled={test.isLoading || !connected}
                style={btn(BLUE)}>Test after save</button>
            </div>
          </Card>

          <Card style={{ padding: 20 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Set up the role in your AWS</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a href={cfnUrl} target="_blank" rel="noreferrer"
                style={{ ...btnFilled('#ff9900'), textDecoration: 'none', textAlign: 'center', color: '#0a0f1e' }}>
                <ExternalLink size={14} /> Launch CloudFormation stack
              </a>
              <a href={cloudConnectionsApi.templateUrl()} target="_blank" rel="noreferrer"
                style={{ ...btn(BLUE), textDecoration: 'none', textAlign: 'center' }}>
                Download SmalBluReadOnly.yaml
              </a>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <Info size={14} color={BLUE} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <strong>Read-only.</strong> The role SmalBlu assumes has zero write/delete/modify permissions.
                  You can revoke access at any time by deleting the IAM role in your account.
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace' }}>
              Trust: arn:aws:iam::892978057206:user/smalblu-orchestrator
            </div>
          </Card>
        </div>
      ) : (
        <Card style={{ padding: 20 }}>
          <div style={{ color: AMBER, fontWeight: 600, fontSize: 13, marginBottom: 12,
                        display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> Static keys are legacy — use the IAM role path unless your security team blocks it.
          </div>
          <Field label="AWS Access Key ID" value={staticKey} onChange={setStaticKey} placeholder="AKIA…" mono />
          <Field label="AWS Secret Access Key" value={staticSecret} onChange={setStaticSecret} placeholder="wJalr…" mono type="password" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Region" value={region} onChange={setRegion} placeholder="us-east-1" />
            <Field label="Account ID (optional)" value={accountId} onChange={setAccountId} placeholder="123456789012" />
          </div>
          <button onClick={() => saveKeys.mutate()} disabled={!staticKey || !staticSecret || saveKeys.isLoading}
            style={btnFilled(BLUE)}>Save static keys</button>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, mono, type = 'text' }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', fontFamily: mono ? 'ui-monospace, monospace' : 'inherit', boxSizing: 'border-box' }} />
    </div>
  );
}
const btn = (c) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
  background: 'rgba(255,255,255,0.04)', border: `1px solid ${c}44`, color: c, cursor: 'pointer',
});
const btnFilled = (c) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center',
  padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
  background: c, color: '#fff', border: 'none', cursor: 'pointer',
});
